import React, { createContext, useContext, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, X, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import DoxHandoffTransition from "@/components/DoxHandoffTransition";

const TrialModalContext = createContext({ openTrial: () => {}, closeTrial: () => {} });

export const useTrialModal = () => useContext(TrialModalContext);

/**
 * TrialModalProvider — exposes `openTrial()` app-wide so ANY CTA
 * ("Start Your 14-Day Free Trial") opens the signup form as a modal
 * overlay without leaving the current page. If the visitor is already
 * logged in, we route them to /dashboard instead.
 */
export function TrialModalProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [prefill, setPrefill] = useState({});
  const [handoff, setHandoff] = useState(null); // { name } while the bridge plays
  const { user, register } = useAuth();
  const navigate = useNavigate();

  const openTrial = useCallback(
    (opts = {}) => {
      if (user) {
        navigate("/dashboard");
        return;
      }
      setPrefill(opts.prefill || {});
      setOpen(true);
    },
    [user, navigate]
  );

  const closeTrial = useCallback(() => setOpen(false), []);

  return (
    <TrialModalContext.Provider value={{ openTrial, closeTrial }}>
      {children}
      {open && (
        <TrialModal
          initial={prefill}
          onClose={closeTrial}
          onDone={(name) => {
            setOpen(false);
            // Bridge the dead moment: play the "Dox is getting ready…"
            // transition BEFORE dropping the visitor into the dashboard.
            setHandoff({ name });
          }}
          register={register}
        />
      )}
      {handoff && (
        <DoxHandoffTransition
          name={handoff.name}
          onDone={() => {
            setHandoff(null);
            toast.success("Welcome to Dox — let's get you compliant.");
            navigate("/dashboard");
          }}
        />
      )}
    </TrialModalContext.Provider>
  );
}

function TrialModal({ initial, onClose, onDone, register }) {
  const [form, setForm] = useState({
    name: initial.name || "",
    company: initial.company || "",
    email: initial.email || "",
    password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await register(form);
    setLoading(false);
    if (res.ok) onDone(form.name);
    else {
      const raw = res.error || "";
      // Sanitize raw backend/pydantic errors into a warm visitor-facing line.
      let friendly = "Something went wrong. Try again.";
      if (/email/i.test(raw) && /(valid|reserved|special-use)/i.test(raw))
        friendly = "Please use a real email address so I can send your login details.";
      else if (/already.*exists|already.*registered/i.test(raw))
        friendly = "Looks like that email already has an account. Try logging in instead.";
      else if (/password/i.test(raw))
        friendly = "That password is too short — please use at least 6 characters.";
      else if (raw && raw.length < 120) friendly = raw;
      setError(friendly);
    }
  };

  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="trial-modal"
      className="fixed inset-0 z-[90] flex items-center justify-center p-4 sm:p-6"
    >
      <div className="absolute inset-0 bg-black/75 backdrop-blur-md" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-3xl glass-strong border border-dox-orange/30 overflow-hidden shadow-2xl shadow-dox-orange/10">
        <div className="absolute -top-24 -right-16 h-64 w-64 rounded-full bg-dox-orange/25 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -left-16 h-60 w-60 rounded-full bg-dox-magenta/20 blur-3xl pointer-events-none" />

        <button
          onClick={onClose}
          aria-label="Close"
          data-testid="trial-modal-close"
          className="absolute right-4 top-4 z-10 h-9 w-9 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-white/70 hover:text-white transition-colors"
        >
          <X size={16} />
        </button>

        <div className="relative p-7 sm:p-8">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-dox-orange/15 text-dox-orange px-3 py-1 text-[11px] font-bold uppercase tracking-wider mb-4">
            <Sparkles size={11} /> 14-day free trial
          </div>
          <h2 className="font-display font-black text-2xl sm:text-3xl text-white leading-tight">
            Let&apos;s put Dox to work.
          </h2>
          <p className="text-white/60 text-sm mt-2 leading-relaxed">
            No credit card. Dox starts reviewing your paperwork the moment you finish this form.
          </p>

          {/* Set expectations for "what happens next" — the biggest source
              of hesitation for first-time visitors. */}
          <ol className="mt-4 space-y-1.5 text-[12px] text-white/60">
            <li className="flex items-start gap-2"><span className="text-dox-orange font-bold mt-0.5">1.</span> Two quick questions from Dox (trade, state, focus).</li>
            <li className="flex items-start gap-2"><span className="text-dox-orange font-bold mt-0.5">2.</span> Your personalized dashboard is ready.</li>
            <li className="flex items-start gap-2"><span className="text-dox-orange font-bold mt-0.5">3.</span> Dox and his team start monitoring in the background.</li>
          </ol>

          <form onSubmit={submit} className="space-y-3 mt-6" data-testid="trial-modal-form">
            <div className="grid grid-cols-2 gap-3">
              <TInput label="Name" testid="trial-name" value={form.name} onChange={upd("name")} />
              <TInput
                label="Company"
                testid="trial-company"
                value={form.company}
                onChange={upd("company")}
                required={false}
              />
            </div>
            <TInput
              label="Email"
              type="email"
              testid="trial-email"
              value={form.email}
              onChange={upd("email")}
            />
            <TInput
              label="Password"
              type="password"
              testid="trial-password"
              value={form.password}
              onChange={upd("password")}
              hint="Minimum 6 characters"
            />
            {error && (
              <p className="text-sm text-red-400" data-testid="trial-modal-error">
                {error}
              </p>
            )}
            <button
              data-testid="trial-modal-submit"
              disabled={loading}
              className="w-full mt-2 rounded-full bg-dox-orange py-3.5 min-h-[52px] font-bold text-white shadow-[0_0_36px_rgba(30,120,255,0.5)] hover:bg-dox-orangeHover disabled:opacity-60 transition-all inline-flex items-center justify-center gap-2"
            >
              {loading ? "Getting your workspace ready…" : (
                <>
                  Start My 14-Day Free Trial <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>

          <div className="text-[11px] text-white/40 mt-4 text-center">
            Already have an account?{" "}
            <button
              type="button"
              onClick={() => {
                onClose();
                window.location.assign("/login");
              }}
              className="text-dox-orange font-semibold hover:underline"
            >
              Log in
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TInput({ label, type = "text", value, onChange, testid, required = true, hint }) {
  return (
    <label className="block">
      <span className="text-[10px] font-semibold text-white/50 uppercase tracking-wider mb-1 block">
        {label}
      </span>
      <input
        data-testid={testid}
        required={required}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white outline-none focus:border-dox-orange focus:ring-2 focus:ring-dox-orange/20 transition text-sm"
      />
      {hint && <span className="text-[10px] text-white/35 mt-1 block">{hint}</span>}
    </label>
  );
}
